// Shared search/filter experience — the SINGLE source of truth for how search
// works across the whole app (Contacts, Subscribers, and anything added later).
//
// DESIGN CONTRACT (keep every list search identical):
//   - One text input, labelled "Search <things>", with a descriptive placeholder.
//   - Fully CLIENT-SIDE: fetch the set once, build a lowercase text "blob" per
//     item, and filter on every keystroke with blob.includes(term). No per-
//     keystroke server calls, no debounce needed (in-memory is instant).
//   - Optional label/category filter pills that AND with the text term.
//   - A muted one-line hint explaining what fields are matched.
//   - An empty-state message when nothing matches.
//
// To add search to a new list, call buildSearch() with a blob() accessor and a
// render() callback. Do NOT hand-roll a bespoke search box — reuse this so the
// experience stays uniform everywhere.
import { h } from './dom.js';

export interface SearchPill {
  label: string;
  value: string;
}

export interface SearchOptions<T> {
  // Noun shown in the label/placeholder/empty-state, e.g. "contacts", "subscribers".
  noun: string;
  // Placeholder hint, e.g. "Search by name, company, label, or note text…".
  placeholder: string;
  // Optional muted hint line rendered under the input (may contain safe HTML).
  hint?: string;
  // Lowercased searchable text for an item (name + company + labels + …).
  blob: (item: T) => string;
  // Optional pill filters. `pillsFor` returns the distinct values; `itemMatchesPill`
  // decides membership. When omitted, no pills are shown.
  pillsFor?: (items: T[]) => SearchPill[];
  itemMatchesPill?: (item: T, value: string) => boolean;
  // Render the filtered items into the results container.
  render: (matches: T[]) => void;
  // Optional FIELD accessors for scoped queries like "id:<value>". When the search
  // term is a single `field:value` token whose field is present here, matching is
  // restricted to that field (substring, case-insensitive) instead of the full-text
  // blob. Unknown fields fall back to normal blob search. For now callers wire only
  // `id` (the username/handle).
  fields?: Record<string, (item: T) => string>;
}

export interface SearchController<T> {
  // The full DOM block: label+input, pills, hint. Append this where search belongs.
  el: HTMLElement;
  // The results container the caller renders into (via options.render).
  // The caller decides its layout (grid, table body wrapper, etc.).
  setItems: (items: T[]) => void;
  // Re-run the current filter (e.g. after an item is removed).
  refresh: () => void;
  input: HTMLInputElement;
}

let uid = 0;

// parseFieldQuery detects a scoped `field:value` query. Returns the lowercased
// field key + value when the raw term starts with a known field prefix, else null
// (meaning: use normal full-text search). Exported for unit testing.
export function parseFieldQuery(raw: string, knownFields: string[]): { field: string; value: string } | null {
  const m = /^([a-z_]+):(.*)$/is.exec(raw.trim());
  if (!m) return null;
  const field = m[1].toLowerCase();
  if (!knownFields.includes(field)) return null;
  return { field, value: m[2].trim().toLowerCase() };
}

// buildSearch wires the shared search UX and returns a controller. The caller
// owns the results container and passes a render() that fills it.
export function buildSearch<T>(opts: SearchOptions<T>): SearchController<T> {
  const id = 'search-' + ++uid;
  const input = h('input', {
    class: 'input', id, type: 'text',
    placeholder: opts.placeholder,
  }) as HTMLInputElement;
  const inputField = h('div', { class: 'field' },
    h('label', { class: 'label', for: id }, `Search ${opts.noun}`), input);

  const pillRow = h('div', { class: 'pill-row', style: 'margin-bottom:18px' });
  const hintEl = opts.hint
    ? h('p', { class: 'muted', style: 'margin-bottom:16px' }, h('small', { html: opts.hint }))
    : null;

  const el = h('div', {}, inputField, pillRow, ...(hintEl ? [hintEl] : []));

  let items: T[] = [];
  let index: { item: T; blob: string }[] = [];
  let activePill = '';

  const renderPills = () => {
    if (!opts.pillsFor) { pillRow.style.display = 'none'; return; }
    const pills = opts.pillsFor(items);
    if (!pills.length) { pillRow.style.display = 'none'; return; }
    pillRow.style.display = '';
    pillRow.replaceChildren(
      pill('All', '', activePill === '', () => { activePill = ''; renderPills(); apply(); }),
      ...pills.map((p) => pill(p.label, p.value, activePill === p.value,
        () => { activePill = p.value; renderPills(); apply(); })),
    );
  };

  const apply = () => {
    const raw = input.value.trim();
    // Scoped field query: a leading `field:value` token (e.g. "id:alice") restricts
    // matching to that single field when the caller registered it in opts.fields.
    const scoped = opts.fields ? parseFieldQuery(raw, Object.keys(opts.fields)) : null;
    const term = raw.toLowerCase();
    const matches = index
      .filter(({ item, blob }) => {
        const textOk = scoped
          ? (!scoped.value || opts.fields![scoped.field](item).toLowerCase().includes(scoped.value))
          : (!term || blob.includes(term));
        return textOk && (!activePill || !opts.itemMatchesPill || opts.itemMatchesPill(item, activePill));
      })
      .map(({ item }) => item);
    opts.render(matches);
  };

  input.addEventListener('input', apply);

  return {
    el,
    input,
    setItems(next: T[]) {
      items = next;
      index = next.map((item) => ({ item, blob: opts.blob(item).toLowerCase() }));
      renderPills();
      apply();
    },
    refresh: apply,
  };
}

// emptyState is the shared "nothing matches" line. Callers append it to their
// results container when render() receives an empty array.
export function emptyState(noun: string): HTMLElement {
  return h('p', { class: 'muted' }, `No ${noun} match.`);
}

function pill(label: string, _value: string, active: boolean, onClick: () => void): HTMLElement {
  return h('a', { href: '#', onclick: (e: Event) => { e.preventDefault(); onClick(); } },
    h('span', { class: 'badge ' + (active ? 'badge-brand' : '') }, label));
}
